import React, { useState } from "react";
import { Box } from "@mui/material";
import { renderFormField } from "../../utils/formFieldRenderer";
import { DataCell } from "./TableStyles";
import IconButton from "@mui/material/IconButton";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ResourceAllocation from "../ResourceAllocation/ResourceAllocation";

const parseTimeInput = (value) => {
  if (!value) return "";
  const regex = /^\d*\.?\d{0,2}$/;
  if (!regex.test(value)) return "";

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "";

  const hours = Math.floor(numValue);
  const minutes = Math.round((numValue - hours) * 100);

  if (minutes >= 60) {
    const additionalHours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours + additionalHours}.${remainingMinutes}`;
  }

  return value;
};

const EditableRow = ({
  row,
  columns,
  formFields,
  onSave,
  onCancel,
  getOptionsForField,
  alignment,
  fontSize,
  unavailableResourceIds,
  projectDetails,
}) => {
  console.log("EditableRow unavailableResourceIds:", unavailableResourceIds);
  const [formData, setFormData] = useState(() => {
    // Initialize form data with row data
    const initialData = {
      ...row,
      milestone_id: row.milestone_id?.toString(),
      estimated_hours:
        row.estimated_hours !== undefined && row.estimated_hours !== null
          ? row.estimated_hours.toString()
          : "",
      project_start_date: projectDetails?.proposed_start_date || null,
      project_end_date: projectDetails?.proposed_end_date || null,
    };
    
    // Calculate allocation_pc for resource mappings
    if (initialData.resource_mappings && Array.isArray(initialData.resource_mappings)) {
      const estimatedHours = parseFloat(initialData.estimated_hours) || 0;
      initialData.resource_mappings = initialData.resource_mappings.map(resource => {
        const resourceHours = parseFloat(resource.resource_budget_hours || resource.allocation_hours) || 0;
        let allocationPc = 0;
        if (estimatedHours > 0) {
          allocationPc = (resourceHours / estimatedHours) * 100;
          allocationPc = Math.round(allocationPc * 100) / 100;
        }
        return {
          ...resource,
          allocation_pc: allocationPc,
          allocation_hours: resourceHours,
        };
      });
    }
    
    if (initialData.milestone_id) {
      const milestoneOptions = getOptionsForField("milestones") || [];
      const selectedMilestone = milestoneOptions.find(
        (m) => m.value.toString() === initialData.milestone_id.toString()
      );
      // Store both start and end dates from milestone
      initialData.milestone_start_date =
        selectedMilestone?.milestone_start_date;
      initialData.milestone_end_date = selectedMilestone?.milestone_end_date;
    }
    return initialData;
  });
  console.log("formData", formData);
  const [errors, setErrors] = React.useState({});
  const [touched, setTouched] = React.useState({});
  const [isValid, setIsValid] = React.useState(true);
  console.log("formData", formData);
  React.useEffect(() => {
    if (row.isNew) {
      const projectId = localStorage.getItem("currentProjectId");
      if (projectId) {
        const storageKey = `tempRows_${projectId}`;
        const savedTempRows = JSON.parse(
          localStorage.getItem(storageKey) || "[]"
        );
        const existingRow = savedTempRows.find(
          (tempRow) => tempRow.task_id === row.task_id
        );

        if (existingRow) {
          setFormData(existingRow);
          validateFormData(existingRow);
        }
      }
    }
  }, [row]);

  React.useEffect(() => {
    if (formData.estimated_hours && formData.resource_mappings?.length > 0) {
      const estimatedHours = parseFloat(formData.estimated_hours) || 0;

      const isUsingPercentages = formData.resource_mappings.some(
        (resource) => resource.resource_budget_hours !== undefined
      );

      if (isUsingPercentages) {
        const updatedMappings = formData.resource_mappings.map((resource) => {
          const resourceHours = resource.resource_budget_hours || 0;
          let allocationPc = 0;
          if (estimatedHours > 0) {
            allocationPc = (resourceHours / estimatedHours) * 100;
            allocationPc = Math.round(allocationPc * 100) / 100;
          }
          return {
            ...resource,
            allocation_hours: resourceHours,
            allocation_pc: allocationPc,
          };
        });

        setFormData((prev) => ({
          ...prev,
          resource_mappings: updatedMappings,
        }));
      }

      validateFormData({
        ...formData,
        estimated_hours: estimatedHours,
      });
    }
  }, [formData.estimated_hours]);

  const saveTempRowToStorage = (data) => {
    if (data.task_id?.startsWith("temp-")) {
      const projectId = localStorage.getItem("currentProjectId");
      if (projectId) {
        const storageKey = `tempRows_${projectId}`;
        const savedTempRows = JSON.parse(
          localStorage.getItem(storageKey) || "[]"
        );

        const updatedTempRows = savedTempRows
          .filter((tempRow) => tempRow.task_id !== data.task_id)
          .concat(data);

        localStorage.setItem(storageKey, JSON.stringify(updatedTempRows));
      }
    }
  };

  const validateDynamicField = (fieldName, value, fieldConfig) => {
    if (!fieldConfig) return "";

    console.log(`Validating field: ${fieldName}`, {
      value,
      required: fieldConfig.required,
      type: fieldConfig.type,
    });

    if (
      fieldConfig.required &&
      (value === undefined || value === null || value === "")
    ) {
      console.log(`${fieldName} is required but empty`);
      return `${fieldConfig.label || fieldName} is required`;
    }

    switch (fieldConfig.type) {
      case "date":
        if (value && fieldConfig.validation) {
          // Handle Unix timestamp (milliseconds) - check if it's a valid timestamp
          let dateValue;
          try {
            if (typeof value === 'number' || /^\d{13,}$/.test(String(value))) {
              const timestamp = parseInt(value);
              // Validate timestamp range (reasonable date range)
              if (timestamp > 0 && timestamp < 9999999999999) {
                dateValue = new Date(timestamp);
              } else {
                console.warn("Invalid timestamp value:", value);
                return "Invalid date format";
              }
            } else {
              dateValue = new Date(value);
            }
            
            // Check if the date is valid
            if (isNaN(dateValue.getTime())) {
              console.warn("Invalid date value:", value);
              return "Invalid date format";
            }
          } catch (e) {
            console.warn("Error converting date:", value, e);
            return "Invalid date format";
          }
          
          const { validation } = fieldConfig;

          // Project date validation
          if (projectDetails) {
            const projectStartDate = new Date(projectDetails.proposed_start_date);
            const projectEndDate = new Date(projectDetails.proposed_end_date);
            
            // Validate project dates
            if (isNaN(projectStartDate.getTime()) || isNaN(projectEndDate.getTime())) {
              console.warn("Invalid project dates:", projectDetails.proposed_start_date, projectDetails.proposed_end_date);
              break;
            }
            
            // Set time to midnight for proper date comparison
            projectStartDate.setHours(0, 0, 0, 0);
            projectEndDate.setHours(0, 0, 0, 0);
            dateValue.setHours(0, 0, 0, 0);

            // Check if date is within project range
            if (dateValue < projectStartDate) {
              return `Date cannot be before project start date (${projectStartDate.toLocaleDateString()})`;
            }
            if (dateValue > projectEndDate) {
              return `Date cannot be after project end date (${projectEndDate.toLocaleDateString()})`;
            }
          }

          // Handle specific validation keys
          if (validation.min === "project_start_date" && projectDetails) {
            const projectStartDate = new Date(projectDetails.proposed_start_date);
            if (!isNaN(projectStartDate.getTime())) {
              projectStartDate.setHours(0, 0, 0, 0);
              if (dateValue < projectStartDate) {
                return validation.message || `Date cannot be before project start date (${projectStartDate.toLocaleDateString()})`;
              }
            }
          }

          if (validation.max === "project_end_date" && projectDetails) {
            const projectEndDate = new Date(projectDetails.proposed_end_date);
            if (!isNaN(projectEndDate.getTime())) {
              projectEndDate.setHours(0, 0, 0, 0);
              if (dateValue > projectEndDate) {
                return validation.message || `Date cannot be after project end date (${projectEndDate.toLocaleDateString()})`;
              }
            }
          }

          if (validation.min === "today") {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (dateValue < today) {
              return validation.message || "Date cannot be in the past";
            }
          }

          if (validation.min === "start_date" && fieldName === "end_date") {
            const startDate = new Date(formData.start_date);
            if (!isNaN(startDate.getTime())) {
              if (dateValue < startDate) {
                return validation.message || "End date must be after start date";
              }
            }
          }
        }
        break;

      case "number":
        if (value && fieldConfig.validation) {
          const numValue = parseFloat(value);
          const { validation } = fieldConfig;

          if (validation.min !== undefined && numValue < validation.min) {
            return (
              validation.message || `Value must be at least ${validation.min}`
            );
          }
          if (validation.max !== undefined && numValue > validation.max) {
            return (
              validation.message || `Value must not exceed ${validation.max}`
            );
          }
        }
        break;

      case "array":
        console.log(`Validating array field: ${fieldName}`, {
          value,
          fieldConfig,
        });

        if (
          fieldConfig.required &&
          (!value || !Array.isArray(value) || value.length === 0)
        ) {
          console.log(`Array field ${fieldName} is required but empty`);
          return `At least one ${fieldConfig.itemTitle || "item"} is required`;
        }

        if (Array.isArray(value) && value.length > 0) {
          const itemErrors = value
            .map((item, index) => {
              const errors = {};
              console.log(`Validating array item ${index}:`, item);

              if (fieldName === "resource_mappings") {
                if (!item.resource_id || item.resource_id === "") {
                  errors.resource_id = "Resource is required";
                }
                if (
                  item.allocation_hours === undefined ||
                  item.allocation_hours === ""
                ) {
                  errors.allocation_hours = "Allocation hours is required";
                }
              }

              Object.entries(fieldConfig.fields).forEach(([key, field]) => {
                if (
                  key === "allocation_pc" &&
                  item.allocation_hours !== undefined
                ) {
                  return;
                }

                if (
                  field.required &&
                  (item[key] === undefined || item[key] === "")
                ) {
                  console.log(`Required field ${key} missing in item ${index}`);
                  errors[key] = `${field.label} is required`;
                }

                if (field.type === "number" && item[key] && field.validation) {
                  const numValue = parseFloat(item[key]);
                  console.log(`Validating number field ${key}:`, {
                    value: numValue,
                    validation: field.validation,
                  });

                  if (
                    field.validation.min !== undefined &&
                    numValue < field.validation.min
                  ) {
                    errors[key] =
                      field.validation.message ||
                      `Must be at least ${field.validation.min}`;
                  }
                  if (
                    field.validation.max !== undefined &&
                    numValue > field.validation.max
                  ) {
                    errors[key] =
                      field.validation.message ||
                      `Must not exceed ${field.validation.max}`;
                  }
                }
              });
              return Object.keys(errors).length > 0 ? errors : null;
            })
            .filter(Boolean);

          if (itemErrors.length > 0) {
            console.log(`Array field ${fieldName} has errors:`, itemErrors);
            return itemErrors;
          }

          // Add validation for total allocation hours
          if (fieldName === "resource_mappings" && formData.estimated_hours) {
            const estimatedHours = parseFloat(formData.estimated_hours) || 0;
            const totalAllocatedHours = value.reduce((sum, item) => {
              return sum + (parseFloat(item.allocation_hours) || 0);
            }, 0);

            // Check if total allocation matches estimated hours
            if (Math.abs(totalAllocatedHours - estimatedHours) > 0.01) {
              // Using small epsilon for float comparison
              console.log(
                `Total allocation (${totalAllocatedHours}) doesn't match estimated hours (${estimatedHours})`
              );
              return `Total allocation (${totalAllocatedHours.toFixed(
                2
              )}h) must match estimated hours (${estimatedHours}h)`;
            }
          }
        }
        break;

      case "select":
        if (fieldConfig.required && (!value || value === "")) {
          return `Please select ${fieldConfig.label || "a value"}`;
        }
        break;

      case "text":
        break;

      default:
        break;
    }

    if (fieldConfig.validation) {
      const { minEqual, message } = fieldConfig.validation;

      if (minEqual && formData[minEqual]) {
        const startDate = new Date(formData[minEqual]);
        const endDate = new Date(value);

        // Set time to midnight for proper date comparison
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        if (endDate < startDate) {
          return message || `Must be equal to or after ${minEqual}`;
        }
      }
    }

    return "";
  };

  const validateFormData = (data) => {
    console.log("Validating form data:", data);
    const newErrors = {};
    let formIsValid = true;

    // Only validate required fields
    columns.forEach((column) => {
      if (column.formField?.required) {
        const error = validateDynamicField(
          column.id,
          data[column.id],
          column.formField
        );
        if (error) {
          console.log(`Validation error for ${column.id}:`, error);
          newErrors[column.id] = error;
          formIsValid = false;
        }
      }
    });

    // Special validation for resource allocation
    if (data.resource_mappings && data.estimated_hours) {
      const estimatedHours = parseFloat(data.estimated_hours) || 0;
      const totalAllocatedHours = data.resource_mappings.reduce((sum, item) => {
        return sum + (parseFloat(item.allocation_hours) || 0);
      }, 0);

      // Check if total allocation matches estimated hours
      if (Math.abs(totalAllocatedHours - estimatedHours) > 0.01) {
        if (!newErrors.resource_mappings) {
          newErrors.resource_mappings = `Total allocation (${totalAllocatedHours}h) must match estimated hours (${estimatedHours}h)`;
          formIsValid = false;
        }
      }
    }

    console.log("Validation results:", {
      errors: newErrors,
      isValid: formIsValid,
    });

    setErrors(newErrors);
    setIsValid(formIsValid);
    return formIsValid;
  };

  const handleChange = (fieldName, value) => {
    console.log(`Field changed: ${fieldName}`, {
      newValue: value,
      previousValue: formData[fieldName],
    });

    let newFormData = {
      ...formData,
      [fieldName]: value,
    };

    if (fieldName === "estimated_hours") {
      const parsedValue = parseTimeInput(value);
      if (parsedValue !== "") {
        newFormData[fieldName] = parsedValue;
      }
      
      // Recalculate allocation_pc for all resources when estimated hours change
      if (newFormData.resource_mappings && Array.isArray(newFormData.resource_mappings)) {
        const estimatedHours = parseFloat(parsedValue) || 0;
        newFormData.resource_mappings = newFormData.resource_mappings.map(resource => {
          const resourceHours = parseFloat(resource.allocation_hours) || 0;
          let allocationPc = 0;
          if (estimatedHours > 0) {
            allocationPc = (resourceHours / estimatedHours) * 100;
            allocationPc = Math.round(allocationPc * 100) / 100;
          }
          return {
            ...resource,
            allocation_pc: allocationPc,
          };
        });
      }
    } else if (fieldName === "resource_mappings") {
      // Calculate allocation_pc for each resource when resource mappings change
      const estimatedHours = parseFloat(newFormData.estimated_hours) || 0;
      if (Array.isArray(value)) {
        newFormData.resource_mappings = value.map(resource => {
          const resourceHours = parseFloat(resource.allocation_hours) || 0;
          let allocationPc = 0;
          if (estimatedHours > 0) {
            allocationPc = (resourceHours / estimatedHours) * 100;
            allocationPc = Math.round(allocationPc * 100) / 100;
          }
          return {
            ...resource,
            allocation_pc: allocationPc,
          };
        });
      }
    } else if (fieldName === "milestone_id") {
      newFormData.start_date = "";
      newFormData.end_date = "";
      newFormData.estimated_hours = "";
      const milestoneOptions = getOptionsForField("milestones") || [];
      const selectedMilestone = value
        ? milestoneOptions.find(
            (m) => m?.value?.toString() === value?.toString()
          )
        : null;
      newFormData.milestone_start_date =
        selectedMilestone?.milestone_start_date || null;
      newFormData.milestone_end_date =
        selectedMilestone?.milestone_end_date || null;
    }
    setFormData(newFormData);
    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));

    saveTempRowToStorage(newFormData);
    validateFormData(newFormData);
  };

  const handleSave = async () => {
    const newTouched = {};
    console.log("columns", columns);
    columns.forEach((column) => {
      if (column.formField) {
        newTouched[column.id] = true;
      }
    });
    setTouched(newTouched);

    if (!validateFormData(formData)) {
      return;
    }

    const processedData = { ...formData };
    columns.forEach((column) => {
      if (column.formField?.type === "number" && processedData[column.id]) {
        processedData[column.id] = parseFloat(processedData[column.id]);
      }
    });

    if (processedData.task_id?.startsWith("temp-")) {
      const projectId = localStorage.getItem("currentProjectId");
      if (projectId) {
        const storageKey = `tempRows_${projectId}`;
        const savedTempRows = JSON.parse(
          localStorage.getItem(storageKey) || "[]"
        );
        const updatedTempRows = savedTempRows.filter(
          (tempRow) => tempRow.task_id !== processedData.task_id
        );
        localStorage.setItem(storageKey, JSON.stringify(updatedTempRows));
      }
    }

    onSave(processedData);
  };

  return (
    <>
      {columns.map((column) => {
        const fieldConfig = column.formField || formFields[column.id];

        if (!fieldConfig) {
          return (
            <DataCell key={column.id} align={alignment} fontSize={fontSize}>
              {column.render
                ? column.render(row[column.id], row)
                : row[column.id]}
            </DataCell>
          );
        }

        const resolvedDisabled =
          typeof fieldConfig.disabled === "function"
            ? fieldConfig.disabled(row)
            : fieldConfig.disabled;
        const resolvedReadOnly =
          typeof fieldConfig.readOnly === "function"
            ? fieldConfig.readOnly(row)
            : fieldConfig.readOnly;

        if (fieldConfig.type === "array" && fieldConfig.fields) {
          return (
            <DataCell
              key={column.id}
              align={alignment}
              fontSize={fontSize}
              sx={{ padding: "8px" }}
            >
              <ResourceAllocation
                value={formData[column.id] || []}
                onChange={(newValue) => handleChange(column.id, newValue)}
                fieldConfig={fieldConfig}
                getOptionsForField={getOptionsForField}
                unavailableResourceIds={unavailableResourceIds}
                budgetHours={parseFloat(formData.estimated_hours) || 0}
              />
            </DataCell>
          );
        }

        const fieldProps = {
          fieldName: column.id,
          fieldConfig: {
            ...fieldConfig,
            label: "",
            size: { xs: 12, sm: 12 },
            ...(resolvedDisabled !== undefined && {
              disabled: resolvedDisabled,
            }),
            ...(resolvedReadOnly !== undefined && {
              readOnly: resolvedReadOnly,
            }),
            sx: {
              "& .MuiFormLabel-root": {
                display: "none",
              },
              "& .MuiInputBase-root": {
                marginTop: 0,
              },
            },
            // Add project date validation for date fields
            ...(fieldConfig.type === "date" && {
              minDate: new Date(formData.project_start_date),
              maxDate: new Date(formData.project_end_date),
            }),
          },
          formData,
          errors,
          touched,
          handleChange,
          handleBlur: () => {},
          setFieldValue: (field, value) => handleChange(field, value),
          setFieldTouched: () => {},
          options:
            fieldConfig.source === "task_status_code"
              ? getOptionsForField?.(
                  "task_status_code",
                  row.isNew || row.task_id?.toString().startsWith("temp-")
                ) || []
              : getOptionsForField?.(fieldConfig.source) || [],
          isEdit: true,
        };

        return (
          <DataCell
            key={column.id}
            align={alignment}
            fontSize={fontSize}
            sx={{
              padding: "4px 8px",
              "& .MuiInputBase-root": {
                height: "32px",
                fontSize: fontSize,
                margin: 0,
                ...(fieldConfig.type === "date" && {
                  width: "150px",
                }),
                ...(fieldConfig.type === "text" && {
                  width: "200px",
                }),
              },
              "& .MuiSelect-select": {
                padding: "4px 8px",
              },
              "& .MuiFormControl-root": {
                margin: 0,
              },
              "& .MuiFormHelperText-root": {
                display: "none",
              },
              "& input[type=number]": {
                "-webkit-appearance": "textfield",
                "-moz-appearance": "textfield",
                appearance: "textfield",
                "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": {
                  "-webkit-appearance": "none",
                  margin: 0,
                },
              },
            }}
          >
            {renderFormField(fieldProps)}
          </DataCell>
        );
      })}
      <DataCell align="right">
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
          <IconButton
            size="small"
            onClick={handleSave}
            disabled={
              !isValid ||
              Object.keys(errors).length > 0 ||
              (formData.estimated_hours !== undefined &&
                formData.resource_mappings?.length > 0 &&
                parseFloat(formData.estimated_hours) !== 0 &&
                (Math.abs(
                  formData.resource_mappings.reduce(
                    (sum, item) =>
                      sum + (parseFloat(item.allocation_hours) || 0),
                    0
                  ) - (parseFloat(formData.estimated_hours) || 0)
                ) > 0.01 ||
                  formData.resource_mappings.some(
                    (resource) =>
                      !resource.resource_id ||
                      resource.resource_id === "" ||
                      resource.allocation_hours === undefined ||
                      resource.allocation_hours === ""
                  )))
            }
            sx={{
              color: "success.main",
              "&:hover": {
                backgroundColor: "success.lighter",
              },
              "&.Mui-disabled": {
                color: "success.main",
                opacity: 0.5,
              },
            }}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onCancel}
            sx={{
              color: "error.main",
              "&:hover": {
                backgroundColor: "error.lighter",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DataCell>
    </>
  );
};

export default EditableRow;
